import { EasyErrorFragment } from "@/src/shared/graphql/fragments/error.generated";
import { computed, ComputedRef, nextTick, ref, Ref } from "vue";

interface DSField {
  errorMessages: Ref<string[] | undefined>;
  validate: () => undefined | string[];
}

export interface DSFormStore {
  formErrorMessages: ComputedRef<string[]>;
  registerField: (name: string, validate: () => undefined | string[]) => void;
  unregisterField: (name: string) => void;
  validate: () => boolean;
  resetValidation: () => void;
  resetFieldValidation: (fieldName: string) => void;
  getFieldErrorMessages: (fieldName: string) => string[] | undefined;
  setEasyErrors: (errors: EasyErrorFragment[], formElement?: HTMLElement) => void;
  removeFormErrorMessage: (index: number) => void;
}

export const useDSFormStore = (): DSFormStore => {
  const fields: Record<string, DSField> = {};
  const formErrorMessages: Ref<string[]> = ref([]);

  const registerField = (name: string, validate: () => undefined | string[]) => {
    const existingField = fields[name];

    if (existingField) {
      existingField.validate = validate;
      return;
    }

    fields[name] = {
      errorMessages: ref(undefined),
      validate,
    };
  };

  const unregisterField = (name: string) => {
    delete fields[name];
  };

  const validate = (): boolean => {
    let hasErrors = false;

    Object.values(fields).forEach(field => {
      field.errorMessages.value = field.validate();

      if (field.errorMessages.value) {
        hasErrors = true;
      }
    });

    return hasErrors;
  };

  const resetValidation = () => {
    Object.keys(fields).forEach(fieldName => {
      resetFieldValidation(fieldName);
    });

    formErrorMessages.value = [];
  };

  const resetFieldValidation = (fieldName: string) => {
    const field = getField(fieldName);

    if (field) {
      field.errorMessages.value = undefined;
    }
  };

  const getFieldErrorMessages = (fieldName: string): string[] | undefined => {
    return getField(fieldName)?.errorMessages.value;
  };

  const setEasyErrors = async (errors: EasyErrorFragment[], formElement?: HTMLElement) => {
    errors.forEach((error: EasyErrorFragment) => {
      if (error.messages?.length) setEasyError(error.attribute, error.messages);
    });

    await nextTick();
    scrollIntoFirstError(formElement);
  };

  const scrollIntoFirstError = (formElement?: HTMLElement) => {
    const firstError = formElement?.querySelector("[data-ds-error]");
    firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const setEasyError = (name: string, errorMessages: string[]) => {
    const field = getField(name);
    if (field) {
      field.errorMessages.value = [...errorMessages];
      return;
    }

    formErrorMessages.value.push(...errorMessages);
  };

  const getField = (name: string): DSField | undefined => {
    return fields[name];
  };

  const removeFormErrorMessage = (indexOfMessage: number) => {
    formErrorMessages.value = formErrorMessages.value.filter((_, messageIndex) => messageIndex !== indexOfMessage);
  };

  return {
    formErrorMessages: computed(() => formErrorMessages.value),
    registerField,
    unregisterField,
    validate,
    resetValidation,
    resetFieldValidation,
    getFieldErrorMessages,
    setEasyErrors,
    removeFormErrorMessage,
  };
};
